import { zodResolver } from '@hookform/resolvers/zod';
import { Check, Download, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { CustomerLanguageSelector } from '@/components/CustomerLanguageSelector';
import { FormatSelector } from '@/components/FormatSelector';
import { Button } from '@/components/ui/Button';
import { useDownloadProductSeoCSV } from '@/hooks/useDownloadProductSeoCSV';
import i18n from '@/lib/i18n';
import { Format } from '@/services/ProductsService';

const schema = z.object({
  language: z
    .string()
    .min(
      1,
      i18n.t(
        'pages.SeoContent.components.DownloadButton.validation.languageRequired',
      ),
    ),
  format: z.enum([Format.CSV, Format.XLSX], {
    required_error: i18n.t(
      'pages.SeoContent.components.DownloadButton.validation.formatRequired',
    ),
  }),
});

type FormData = z.infer<typeof schema>;

export function DownloadSelector() {
  const [url, setUrl] = useState<string | null>(null);

  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.SeoContent.components.DownloadButton',
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      format: Format.CSV,
    },
  });

  const isLanguageSelected = watch('language');

  const { downloadProductSeoCSV } = useDownloadProductSeoCSV();

  function handleDownload() {
    if (url) {
      window.open(url);
      setUrl(null);
    }
  }

  const onSubmit = handleSubmit(async ({ language, format }) => {
    try {
      const data = await downloadProductSeoCSV({ language, format });

      if (data.url) {
        setUrl(data.url);
      }
    } catch {
      toast.error(t('downloadError'));
    }
  });

  return (
    <div className="flex items-center justify-end">
      <form className="flex items-center gap-2" onSubmit={onSubmit}>
        <div className="w-[160px]">
          <Controller
            control={control}
            name="language"
            render={({ field }) => (
              <CustomerLanguageSelector
                disabled={isSubmitting}
                value={field.value}
                onValueChange={field.onChange}
              />
            )}
          />
        </div>

        {isLanguageSelected && (
          <div className="w-[160px]">
            <Controller
              control={control}
              name="format"
              render={({ field }) => (
                <FormatSelector
                  disabled={isSubmitting}
                  value={field.value}
                  onValueChange={field.onChange}
                />
              )}
            />
          </div>
        )}

        {!isSubmitting && !url && (
          <Button type="submit" disabled={!isValid} className="w-auto">
            <Download className="h-4 w-4" /> {t('downloadLabelGenerate')}
          </Button>
        )}

        {!isSubmitting && url && (
          <Button
            type="button"
            className="w-auto bg-green-600 hover:bg-green-700"
            onClick={handleDownload}
          >
            <Check className="h-4 w-4" /> {t('downloadLabelReady')}
          </Button>
        )}

        {isSubmitting && (
          <Button type="button" className="w-auto">
            <Loader2 className="animate-spin" />
          </Button>
        )}
      </form>
    </div>
  );
}
